import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageHeader } from '../design';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  FileText, 
  Search, 
  Filter, 
  RefreshCw, 
  Download, 
  ChevronLeft, 
  ChevronRight, 
  AlertTriangle,
  CheckCircle,
  Info,
  X,
  Clock,
  User,
  Building,
  Shield,
  Activity,
  BarChart3,
  Copy,
  ExternalLink,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { governanceAPI } from '@/lib/governanceApi';
import { format, formatDistanceToNow, parseISO, subDays } from 'date-fns';

export default function Audit() {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_count: 0,
    total_pages: 0
  });
  const [aggregations, setAggregations] = useState({
    by_action: {},
    by_scope: {},
    by_severity: { critical: 0, warning: 0, info: 0 },
    trend: []
  });
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    tenants: []
  });
  
  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  
  // UI State
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('logs');
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  
  // Get date range
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate = null;
    
    switch (dateRange) {
      case '7d':
        startDate = subDays(now, 7);
        break;
      case '30d':
        startDate = subDays(now, 30);
        break;
      case '90d':
        startDate = subDays(now, 90);
        break;
      default:
        startDate = null;
    }
    
    return startDate ? startDate.toISOString() : null;
  }, [dateRange]);
  
  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page,
        page_size: pagination.page_size,
        search: debouncedSearch || undefined,
        action: actionFilter !== 'all' ? actionFilter : undefined,
        scope: scopeFilter !== 'all' ? scopeFilter : undefined,
        result: resultFilter !== 'all' ? resultFilter : undefined,
        user_id: userFilter !== 'all' ? userFilter : undefined,
        tenant_id: tenantFilter !== 'all' ? tenantFilter : undefined,
        start_date: getDateRange()
      };
      
      const response = await governanceAPI.getUnifiedAuditLogs(params);
      const data = response.data;
      
      setLogs(data.logs || []);
      setPagination(data.pagination || pagination);
      setAggregations(data.aggregations || aggregations);
      setFilterOptions(data.filter_options || filterOptions);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError('Failed to load audit logs. Please try again.');
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, actionFilter, scopeFilter, resultFilter, userFilter, tenantFilter, getDateRange, pagination.page_size]);
  
  // Initial fetch
  useEffect(() => {
    fetchAuditLogs(1);
  }, [debouncedSearch, actionFilter, scopeFilter, resultFilter, userFilter, tenantFilter, dateRange]);
  
  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchAuditLogs(pagination.page);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, pagination.page, fetchAuditLogs]);
  
  // Export handler
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await governanceAPI.exportAuditLogs(format, {
        action: actionFilter !== 'all' ? actionFilter : undefined,
        scope: scopeFilter !== 'all' ? scopeFilter : undefined,
        start_date: getDateRange()
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'json' ? 'application/json' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Audit logs exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };
  
  // Clear filters
  const clearFilters = () => {
    setSearch('');
    setActionFilter('all');
    setScopeFilter('all');
    setResultFilter('all');
    setUserFilter('all');
    setTenantFilter('all');
    setDateRange('30d');
  };
  
  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  
  // Get severity badge
  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> Critical</Badge>;
      case 'warning':
        return <Badge variant="outline" className="border-amber-500 text-amber-500 gap-1"><AlertTriangle className="h-3 w-3" /> Warning</Badge>;
      default:
        return <Badge variant="secondary" className="gap-1"><Info className="h-3 w-3" /> Info</Badge>;
    }
  };
  
  // Get result badge
  const getResultBadge = (result) => {
    // Convert result to string if it's an object
    const resultStr = typeof result === 'object' ? (result?.status || JSON.stringify(result)) : String(result || '');
    const lowerResult = resultStr.toLowerCase();
    
    if (['success', 'ok', 'completed'].includes(lowerResult)) {
      return <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1"><CheckCircle className="h-3 w-3" /> Success</Badge>;
    } else if (['failed', 'error', 'blocked', 'rejected'].includes(lowerResult)) {
      return <Badge variant="destructive" className="gap-1"><X className="h-3 w-3" /> Failed</Badge>;
    }
    return <Badge variant="secondary">{resultStr || 'Unknown'}</Badge>;
  };
  
  // Get action color
  const getActionColor = (action) => {
    if (action?.includes('KILL') || action?.includes('DELETE') || action?.includes('BLOCK')) {
      return 'text-red-500';
    }
    if (action?.includes('CREATE') || action?.includes('APPROVE') || action?.includes('RESUME')) {
      return 'text-emerald-500';
    }
    if (action?.includes('UPDATE') || action?.includes('MODIFY') || action?.includes('EDIT')) {
      return 'text-amber-500';
    }
    return 'text-slate-400';
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
      return format(date, 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };
  
  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  };
  
  // Unique actions from aggregations
  const uniqueActions = useMemo(() => {
    return Object.keys(aggregations.by_action || {});
  }, [aggregations.by_action]);
  
  // Unique scopes from aggregations
  const uniqueScopes = useMemo(() => {
    return Object.keys(aggregations.by_scope || {});
  }, [aggregations.by_scope]);
  
  // Trend chart max value
  const trendMax = useMemo(() => {
    return Math.max(...(aggregations.trend || []).map(d => d.count), 1);
  }, [aggregations.trend]);
  
  // Has active filters
  const hasActiveFilters = search || actionFilter !== 'all' || scopeFilter !== 'all' || 
    resultFilter !== 'all' || userFilter !== 'all' || tenantFilter !== 'all';

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="audit-page">
        {/* Header */}
        <PageHeader
          title="Unified Audit Logs"
          subtitle={`Comprehensive audit trail from all system sources • ${pagination.total_count.toLocaleString()} total events`}
          actions={
            <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={autoRefresh ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className="gap-2"
                  data-testid="auto-refresh-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                  {autoRefresh ? 'Auto' : 'Manual'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {autoRefresh ? 'Auto-refresh every 30s' : 'Click to enable auto-refresh'}
              </TooltipContent>
            </Tooltip>
            
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px]" data-testid="date-range-select">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                disabled={exporting}
                data-testid="export-csv-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('json')}
                disabled={exporting}
                data-testid="export-json-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                JSON
              </Button>
            </div>
          </div>
          }
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Events</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="total-events">
                    {loading ? '-' : pagination.total_count.toLocaleString()}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Critical Events</p>
                  <p className="text-2xl font-bold text-red-500" data-testid="critical-events">
                    {loading ? '-' : aggregations.by_severity.critical}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Warnings</p>
                  <p className="text-2xl font-bold text-amber-500" data-testid="warning-events">
                    {loading ? '-' : aggregations.by_severity.warning}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Info Events</p>
                  <p className="text-2xl font-bold text-slate-600 dark:text-slate-300" data-testid="info-events">
                    {loading ? '-' : aggregations.by_severity.info}
                  </p>
                </div>
                <Info className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Trend Chart */}
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Activity Trend (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <div className="flex items-end gap-1 h-24" data-testid="activity-trend-chart">
                {(aggregations.trend || []).map((day, i) => (
                  <Tooltip key={i}>
                    <TooltipTrigger asChild>
                      <div
                        className="flex-1 bg-indigo-500/80 hover:bg-indigo-500 rounded-t transition-all cursor-pointer min-h-[4px]"
                        style={{ 
                          height: `${Math.max((day.count / trendMax) * 100, 4)}%`
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">{day.label}</p>
                      <p className="text-xs text-slate-400">{day.count} events</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search logs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-slate-50 dark:bg-slate-900/50"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[160px]" data-testid="action-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {uniqueActions.slice(0, 15).map(action => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={scopeFilter} onValueChange={setScopeFilter}>
                <SelectTrigger className="w-[140px]" data-testid="scope-filter">
                  <SelectValue placeholder="Scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scopes</SelectItem>
                  {uniqueScopes.map(scope => (
                    <SelectItem key={scope} value={scope}>{scope}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={resultFilter} onValueChange={setResultFilter}>
                <SelectTrigger className="w-[140px]" data-testid="result-filter">
                  <SelectValue placeholder="Result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Results</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-[180px]" data-testid="user-filter">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {filterOptions.users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={tenantFilter} onValueChange={setTenantFilter}>
                <SelectTrigger className="w-[180px]" data-testid="tenant-filter">
                  <Building className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {filterOptions.tenants.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100 dark:bg-slate-800">
            <TabsTrigger value="logs" className="gap-2">
              <FileText className="h-4 w-4" />
              Logs ({pagination.total_count})
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="logs">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[...Array(10)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : error ? (
                  <div className="p-8 text-center">
                    <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                    <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
                    <Button onClick={() => fetchAuditLogs(1)} variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 dark:text-slate-400">
                      {hasActiveFilters ? 'No logs match your filters' : 'No audit logs found'}
                    </p>
                    {hasActiveFilters && (
                      <Button onClick={clearFilters} variant="outline" className="mt-4">
                        Clear Filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <ScrollArea className="h-[600px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-50 dark:bg-slate-800">
                        <TableRow>
                          <TableHead className="w-[180px]">Timestamp</TableHead>
                          <TableHead className="w-[140px]">Action</TableHead>
                          <TableHead className="w-[100px]">Scope</TableHead>
                          <TableHead>Target</TableHead>
                          <TableHead className="w-[180px]">User</TableHead>
                          <TableHead className="w-[100px]">Result</TableHead>
                          <TableHead className="w-[100px]">Severity</TableHead>
                          <TableHead className="w-[80px]">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs.map((log, i) => (
                          <TableRow 
                            key={log.id || `${log.source || 'log'}-${log.timestamp || log.created_at || log.action || 'row'}-${i}`}
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                            onClick={() => {
                              setSelectedLog(log);
                              setDetailModalOpen(true);
                            }}
                            data-testid={`audit-row-${log.id}`}
                          >
                            <TableCell className="font-mono text-xs">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>{formatTimestamp(log.timestamp)}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {formatRelativeTime(log.timestamp)}
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <span className={`font-medium ${getActionColor(log.action)}`}>
                                {log.action}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {log.scope || 'global'}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="truncate block">
                                    {log.target_name || log.target_id || '-'}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{log.target_name}</p>
                                  {log.target_id && log.target_id !== log.target_name && (
                                    <p className="text-xs text-slate-400 font-mono">{log.target_id}</p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2">
                                    <User className="h-3 w-3 text-slate-400" />
                                    <span className="truncate max-w-[140px]">
                                      {log.initiated_by_name || 'System'}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{log.initiated_by_name}</p>
                                  {log.initiated_by_email && (
                                    <p className="text-xs text-slate-400">{log.initiated_by_email}</p>
                                  )}
                                  <p className="text-xs text-slate-400 font-mono mt-1">{log.initiated_by}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{getResultBadge(log.result)}</TableCell>
                            <TableCell>{getSeverityBadge(log.severity)}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLog(log);
                                  setDetailModalOpen(true);
                                }}
                                data-testid={`view-details-${log.id}`}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
                
                {/* Pagination */}
                {!loading && logs.length > 0 && (
                  <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Showing {((pagination.page - 1) * pagination.page_size) + 1} - {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAuditLogs(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Page {pagination.page} of {pagination.total_pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAuditLogs(pagination.page + 1)}
                        disabled={pagination.page >= pagination.total_pages}
                        data-testid="next-page-btn"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="analytics">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Actions Breakdown */}
              <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Top Actions</CardTitle>
                  <CardDescription>Most frequent actions in the selected period</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-2">
                      {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="actions-breakdown">
                      {Object.entries(aggregations.by_action || {}).slice(0, 10).map(([action, count]) => (
                        <div key={action} className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${getActionColor(action)}`}>{action}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-indigo-500 rounded-full"
                                style={{ width: `${(count / Math.max(...Object.values(aggregations.by_action || {}), 1)) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-500 w-10 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Scope Breakdown */}
              <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">By Scope</CardTitle>
                  <CardDescription>Events grouped by scope</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-2">
                      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="scope-breakdown">
                      {Object.entries(aggregations.by_scope || {}).map(([scope, count]) => (
                        <div key={scope} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {scope === 'global' && <Shield className="h-4 w-4 text-indigo-500" />}
                            {scope === 'org' && <Building className="h-4 w-4 text-emerald-500" />}
                            {scope === 'agent' && <Activity className="h-4 w-4 text-amber-500" />}
                            {!['global', 'org', 'agent'].includes(scope) && <FileText className="h-4 w-4 text-slate-400" />}
                            <span className="text-sm font-medium capitalize">{scope}</span>
                          </div>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Sources */}
              <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Data Sources</CardTitle>
                  <CardDescription>Audit logs are aggregated from multiple system sources</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                      <p className="text-xs text-slate-500">superadmin_audit</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">Governance Actions</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                      <p className="text-xs text-slate-500">org_audit_logs</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">Organization Events</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                      <p className="text-xs text-slate-500">ai_activity_logs</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">AI Executions</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                      <p className="text-xs text-slate-500">ai_audit_log</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">Legacy Logs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Audit Log Details
              </DialogTitle>
              <DialogDescription>
                Complete information about this audit event
              </DialogDescription>
            </DialogHeader>
            
            {selectedLog && (
              <ScrollArea className="max-h-[60vh] pr-4">
                <div className="space-y-6">
                  {/* Event Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Action</p>
                      <p className={`font-medium ${getActionColor(selectedLog.action)}`}>
                        {selectedLog.action}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Timestamp</p>
                      <p className="font-medium">{formatTimestamp(selectedLog.timestamp)}</p>
                      <p className="text-xs text-slate-400">{formatRelativeTime(selectedLog.timestamp)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Scope</p>
                      <Badge variant="outline">{selectedLog.scope || 'global'}</Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Result</p>
                      {getResultBadge(selectedLog.result)}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Severity</p>
                      {getSeverityBadge(selectedLog.severity)}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Source</p>
                      <Badge variant="secondary">{selectedLog.source}</Badge>
                    </div>
                  </div>
                  
                  {/* User Info */}
                  <div className="border-t pt-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                      <User className="h-3 w-3" /> Initiated By
                    </p>
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
                      <p className="font-medium">{selectedLog.initiated_by_name || 'System'}</p>
                      {selectedLog.initiated_by_email && (
                        <p className="text-sm text-slate-500">{selectedLog.initiated_by_email}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <code className="text-xs bg-slate-200 dark:bg-slate-800 px-2 py-1 rounded font-mono">
                          {selectedLog.initiated_by}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(selectedLog.initiated_by)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Target Info */}
                  <div className="border-t pt-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                      <Building className="h-3 w-3" /> Target
                    </p>
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
                      <p className="font-medium">{selectedLog.target_name || '-'}</p>
                      {selectedLog.target_id && selectedLog.target_id !== selectedLog.target_name && (
                        <div className="flex items-center gap-2 mt-2">
                          <code className="text-xs bg-slate-200 dark:bg-slate-800 px-2 py-1 rounded font-mono">
                            {selectedLog.target_id}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(selectedLog.target_id)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {selectedLog.tenant_name && (
                        <p className="text-sm text-slate-500 mt-2">
                          Tenant: {selectedLog.tenant_name}
                        </p>
                      )}
                      {selectedLog.org_name && (
                        <p className="text-sm text-slate-500">
                          Organization: {selectedLog.org_name}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Reason */}
                  {selectedLog.reason && (
                    <div className="border-t pt-4">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Reason</p>
                      <p className="text-sm bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
                        {selectedLog.reason}
                      </p>
                    </div>
                  )}
                  
                  {/* Details */}
                  {selectedLog.details && Object.keys(selectedLog.details).some(k => selectedLog.details[k]) && (
                    <div className="border-t pt-4">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Additional Details</p>
                      <pre className="text-xs bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 overflow-auto">
                        {JSON.stringify(selectedLog.details, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {/* Log ID */}
                  <div className="border-t pt-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Log ID</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-200 dark:bg-slate-800 px-2 py-1 rounded font-mono">
                        {selectedLog.id}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(selectedLog.id)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
